import { inject, Injectable, signal, WritableSignal } from '@angular/core';
import { Request } from "@core/models/requests.model";
import { BotIADeleteResponse, BotIAResponse, BotIASelectable } from '@modules/configuration-ia/interfaces/bot-ia/bot-ia.interface';
import { botSettings, botSettingsResponse, botWelcomeTemplates, duplicateBotResponse } from '@modules/configuration-ia/interfaces/global/ai-config-data.interface';
import { AIConfigurationStore } from '../../store/ai-configuration.store';
import { getState } from '@ngrx/signals';
import { AlertType, DynamicComponentService } from '@shared/components/atomics/body-loading/dynamic-component-service.service';
import { openAlertFn } from '@shared/helpers/closures';
import { TranslateService } from '@core/api/translate.service';
import { AITextGenerationService } from '../general/ai-text-generation.service';
import toLocalISOString from '@shared/helpers/functions/get-formatted-date.helper';
import { TrunkBaseConfigurationService } from '@core/services/trunk-base-configuration/trunk-base-configuration.service';
import { SharedStateSelectedFiltersServiceNew } from '../../signals/global/selected-filters/shared-state-selected-filters-NEW.service';
import { StorageService } from '@core/services/storages.service';

@Injectable({
  providedIn: 'root'
})
export class BotIaService {
  private url = 'configuracionia';
  private configUser: any = undefined;
  private readonly aIConfigurationStore = inject(AIConfigurationStore);
  private readonly dynamicComponentService = inject(DynamicComponentService);
  private readonly trunkBaseConfigurationService = inject(TrunkBaseConfigurationService);
  private readonly sharedStateSelectedFiltersServiceNew = inject(SharedStateSelectedFiltersServiceNew);
  private translateService = inject(TranslateService);
  private openAlert = openAlertFn(this.dynamicComponentService, 'htmlContent');
  private loadedUser = false
  private readonly ID_ATTENTION = 12;
  private readonly DEFAULT_RECONTACTO_TEMPLATE = `Hola,

Queremos dar seguimiento a tu interés en nuestros vehículos y servicios.
Si aún estás considerando opciones o necesitas información adicional, con gusto podemos asesorarte y brindarte los detalles que requieras.

Estamos para apoyarte en todo el proceso.
Quedamos atentos a tu mensaje.`;
  public botInboundCount: WritableSignal<number> = signal(0);
  public isSavingTemplates: boolean = false;
  public isSavingPrompting: boolean = false;
  public isSavingPromptingSeekop: boolean = false;
  public isSavingInfoBot: boolean = false

  private nameUser: string = '';

  constructor(
    private request: Request,
    private aiTextGenerationService: AITextGenerationService,
    private storageService: StorageService,
  ) {
    this.setConfigUser();
  }

  private ensureConfigUser() {
    const dbEmpresa = this.configUser?.basesDeDatos?.dbEmpresa;
    if (!this.loadedUser || !this.configUser || !dbEmpresa) {
      this.setConfigUser();
    }

    return this.configUser ?? {};
  }

  public getListConfigurationIa(idMarca: string, idDistribuidor: string, idGrupo: string = '', tipo = '', idSegmento = '', idSubmarca = '') {
    const config = this.ensureConfigUser();
    const { husoHorario, ...configWithoutTimezone } = config;
    const body = {
      configuracion: {
        ...configWithoutTimezone,
      },
      idMarca,
      idDistribuidor,
      idGrupo,
      tipo,
      idSegmento,
      idSubmarca,
      ubicacion: "front"
    }

    return this.request.Post<BotIAResponse>(`${this.url}/getlist-configuration`, body);
  }

  public deleteConfigurationIA(Id: string | number) {
    const configUser = this.ensureConfigUser();
    const body = {
      ...configUser,
    }
    return this.request.Delete<BotIADeleteResponse>(`${this.url}/delete-configuration/${Id}`, body)
  }

  public setConfigUser() {
    const data = this.storageService.getSessionStorage('userInfo') ?? {};
    this.configUser = this.request.getConfigUser() ?? {};
    this.nameUser = data.User ?? data.data?.user ?? '';
    this.loadedUser = true;
  }

  public getConfigurationIaById(Id: string) {
    const configUser = this.ensureConfigUser();
    const body = {
      ...configUser,
    }
    return this.request.Post<botSettingsResponse>(`${this.url}/get-complete/${Id}`, body)
  }
  public updateConfigurationIa(configurationObject: botSettings) {
    const configUser = this.ensureConfigUser();
    const body = {
      ...configurationObject,
      configuracion: configUser
    };
    if (!this.trunkBaseConfigurationService.CIABotIaIdDistribuidorBdc && body.iarequest) {
      delete body.iarequest.idDistribuidorBdc;
    }
    return this.request.Put<botSettingsResponse>(`${this.url}/edit-configuration/${configurationObject.iarequest.idia}`, body)
  }

  public addConfigurationIa(configurationObject: botSettings) {
    const configUser = this.ensureConfigUser();
    //Se setean los datos del usuario y la fecha de creación
    configurationObject.iarequest.idcreado = configUser.IdUsuario ?? '4520';
    configurationObject.iarequest.nombreCreado = configUser.User ?? '';
    configurationObject.iarequest.creado = toLocalISOString();
    configurationObject.horarios.map(horario => {
      horario.creado = toLocalISOString();
      return horario;
    });
    const body: any = {
      ...configurationObject,
      configuracion: configUser
    };
    if (!this.trunkBaseConfigurationService.CIABotIaIdDistribuidorBdc && body.iarequest) {
      delete body.iarequest.idDistribuidorBdc;
    }
    // Agregar nombreCreado a nivel raíz para plantilla de recontacto
    if (configurationObject.promtingRecontacto) {
      body.nombreCreado = this.nameUser;
    }
    return this.request.Post<botSettingsResponse>(`${this.url}/create-configuration`, body)
  }
  public duplicateBotIa(idIa: string) {
    const configUser = this.ensureConfigUser();
    const body = {
      idBot: idIa,
      idUsuario: configUser.IdUsuario ?? '4520',
      nombreCreado: configUser.User ?? '',
      configuracion: { ...configUser }
    }
    return this.request.Post<duplicateBotResponse>(`${this.url}/copy`, body)
  }
  loadListConfigurationIa({
    idDistribuidor = '',
    idMarca = '',
    idGrupo = '',
    tipo = '',
    idSegmentacion = '',
    idSubmarca = '',
    showLoading = false
  }) {
    const configUser = this.ensureConfigUser();
    const idDistribuidorRequest = idDistribuidor ?? configUser.IdDistribuidor ?? '';

    if (showLoading) {
      if (!this.trunkBaseConfigurationService.CIABodyLoadingRequests) this.dynamicComponentService.showBodyLoading();
    }
    this.getListConfigurationIa(idMarca, idDistribuidorRequest, idGrupo, tipo, idSegmentacion, idSubmarca).subscribe({
      next: (res) => {
        const botList = res.response;
        botList.map(bot => {
          if (bot.marca.idMarca == '129') {
            bot.marca.nombre = this.sharedStateSelectedFiltersServiceNew.STELLANTIS_TEXT;
          }
          return bot;
        });
        this.aIConfigurationStore.setIndividualCatalog('botIaList', botList);
        this.botInboundCount.set(this.countBotInbounds());
        if (!this.trunkBaseConfigurationService.CIABodyLoadingRequests) if (showLoading) this.dynamicComponentService.destroyBodyLoading();
      },
      error: (err) => {
        this.aIConfigurationStore.setIndividualCatalog('botIaList', []);
        this.openAlert(this.translateService.instant('unloaded_bot_error', 'Ocurrió un error al cargar los Bots, algunos módulos dejarán de funcionar'), '', AlertType.error, true)
        if (!this.trunkBaseConfigurationService.CIABodyLoadingRequests) if (showLoading) this.dynamicComponentService.destroyBodyLoading();
      }
    });
  }
  public countBotInbounds(): number {
    const state = getState(this.aIConfigurationStore).catalogs.botIaList;
    if (state) {
      return state.filter(bot => bot.tipo == this.ID_ATTENTION).length;
    }
    return 0;
  }
  public loadInitialTemplate(botType: number, type: 'welcome' | 'goodbye', idMarca: string) {
    let text = '';

    if (type === 'welcome') {
      text = `Reescribe el siguiente texto '%s' para generar una plantilla de bienvenida de tipo utilitario para WhatsApp en una agencia o distribuidora automotriz.

        Reglas obligatorias:
        - Conserva intactos todos los datos que ya existen entre [].
        - No agregues nuevos datos entre [].
        - No elimines ningún dato existente entre [].
        - Los corchetes [] solo deben aparecer si ya existen en el texto original. No generes nuevos bajo ninguna circunstancia.
        - El mensaje debe funcionar como una bienvenida inicial del asesor hacia el cliente.
        - Usa un tono cordial, cercano y conversacional.
        - El mensaje debe expresar disposición para ayudar con dudas o información sobre vehículos o servicios.
        - Puede invitar al usuario a continuar la conversación o hacer preguntas.
        - Evita mencionar procesos internos como “hemos recibido tu consulta”, “tu solicitud fue registrada”, “ticket”, “registro”, “sistema” o similares.
        - No incluyas contenido promocional, publicitario o de marketing.
        - No menciones promociones, descuentos, ofertas ni beneficios comerciales.
        - No utilices lenguaje de venta agresivo o persuasivo.
        - El resultado debe sentirse como un saludo natural de un asesor que inicia la conversación.
        - El mensaje debe poder clasificarse claramente como utilitario, no marketing.
        - Devuelve únicamente el texto final reescrito.
      `;
      text = text.replace('%s', (botWelcomeTemplates[botType] ?? 'Bienvenido al bot automotriz de seekop'));
    } else {
      text = `Reescribe el siguiente texto '%s' considerando lo siguiente: -No agregues datos entre []. -No elimines los datos que existen en el texto que se encuentran entre []. -No debe ser una plantilla que parezca de marketing.`;
      text = text.replace('%s', 'A nombre de  [prospectos.distribuidor] agradezco su atención.');
    }

    return new Promise((resolve) => {
      this.aiTextGenerationService.enhanceOrGenerate(
        text, idMarca, 'instruccion'
      ).subscribe({
        next: (res) => {
          resolve(res.mensaje);
        },
        error: () => {
          if (!this.trunkBaseConfigurationService.CIABodyLoadingRequests) this.dynamicComponentService.destroyBodyLoading();
          this.openAlert(this.translateService.instant('error_ia_text_response', 'No se pudo generar el texto, revisar las credenciales del API de IA'), '', AlertType.error, true)
        }
      })
    });
  }
  public getRecontactoTemplate(): string {
    return this.DEFAULT_RECONTACTO_TEMPLATE;
  }

  public loadInitialTemplateStrictDos(botType: number, idMarca: string): Promise<string> {
    const text = `Reescribe el siguiente texto '%s' para generar una plantilla de tipo estrictamente utilitaria para WhatsApp.

Instrucciones obligatorias:
- Mantén intacto cualquier contenido entre []. No modifiques, traduzcas, reordenes ni alteres los valores dentro de [] bajo ninguna circunstancia.
- No agregues nuevos contenidos entre [].
- No elimines contenidos existentes entre [].
- Si el texto original NO contiene elementos entre [], no agregues ninguno.
- No cambies el formato de los corchetes ni generes nuevos placeholders.

- El mensaje debe ser exclusivamente utilitario: informativo, operativo o de atención al usuario.
- Debe leerse como un mensaje de servicio, soporte o acompañamiento, nunca como promoción.
- El mensaje puede saludar, ofrecer ayuda o dar seguimiento, pero sin intención comercial.

- Está estrictamente prohibido:
  - lenguaje promocional o publicitario
  - intención de venta o captación
  - invitaciones a comprar o contratar
  - menciones de promociones, descuentos, ofertas o beneficios
  - frases persuasivas o aspiracionales
  - urgencia comercial
  - contenido tipo campaña o remarketing

- Evita completamente frases relacionadas a procesos internos como:
  "hemos recibido tu solicitud", "tu registro fue exitoso", "ticket", "sistema", etc.

- Usa un tono:
  - neutral
  - profesional
  - cordial
  - conversacional
  - enfocado en atención al usuario

- El mensaje final debe poder clasificarse claramente como utilitario según políticas de WhatsApp (Meta/Twilio).

- Si el texto original contiene lenguaje comercial, conviértelo a un mensaje completamente neutral y de atención, sin cambiar el propósito principal.

- No agregues información nueva que no esté implícita en el texto original.

- Devuelve únicamente el texto final reescrito.
`.replace('%s', (botWelcomeTemplates[botType] ?? 'Bienvenido al bot automotriz de seekop'));

    return new Promise((resolve, reject) => {
      this.aiTextGenerationService.enhanceOrGenerate(text, idMarca, 'instruccion').subscribe({
        next: (res) => resolve(res.mensaje),
        error: () => {
          if (!this.trunkBaseConfigurationService.CIABodyLoadingRequests) this.dynamicComponentService.destroyBodyLoading();
          reject('Error generating strict template Dos');
        }
      });
    });
  }

  public loadInitialTemplateStrictTres(botType: number, idMarca: string): Promise<string> {
    const text = `Reescribe el siguiente texto '%s' mejorando su redacción y adaptándolo libremente, respetando su intención original.

Reglas obligatorias:
- Mantén intacto cualquier contenido entre []. No modifiques ni alteres su contenido.
- No agregues nuevos contenidos entre [].
- No elimines contenidos existentes entre [].
- Si el texto original no contiene [], no agregues ninguno.

- Puedes cambiar la estructura, redacción y forma del mensaje de manera libre para mejorar claridad, naturalidad y fluidez.
- Puedes ajustar el tono para que sea más conversacional, cercano y claro.
- Puedes reorganizar ideas si mejora la comprensión del mensaje.

- El resultado debe mantenerse como un mensaje utilitario:
  - enfocado en atención, información o acompañamiento
  - sin intención comercial

- Evita que el mensaje suene rígido, robótico o genérico.
- Permite variaciones naturales en la forma de saludar o invitar a continuar la conversación.

- No incluyas contenido promocional, publicitario o de marketing.
- No menciones promociones, descuentos, ofertas, beneficios ni incentivos comerciales.
- No utilices lenguaje de venta, persuasión o urgencia comercial.

- Si el texto original tiene un tono comercial, suavízalo a un tono informativo y de atención.

- Devuelve únicamente el texto final reescrito.
`.replace('%s', (botWelcomeTemplates[botType] ?? 'Bienvenido al bot automotriz de seekop'));

    return new Promise((resolve, reject) => {
      this.aiTextGenerationService.enhanceOrGenerate(text, idMarca, 'instruccion').subscribe({
        next: (res) => resolve(res.mensaje),
        error: () => {
          if (!this.trunkBaseConfigurationService.CIABodyLoadingRequests) this.dynamicComponentService.destroyBodyLoading();
          reject('Error generating strict template Tres');
        }
      });
    });
  }


  public generateStrictTemplateDosFromText(sourceText: string, idMarca: string): Promise<string> {
    const text = `Reescribe el siguiente texto '%s' para una plantilla utilitaria de WhatsApp.

Reglas de la plantilla Dos (conservadora):
- Mantener intacto cualquier contenido entre [].
- No agregar ni eliminar contenidos entre [].
- Conservar la intencion y orden general del mensaje original.
- Hacer cambios minimos de estilo y claridad, sin cambios drasticos.
- Mantener tono cordial, claro y de servicio.
- Prohibido contenido comercial: promociones, ofertas, descuentos o lenguaje de venta.
- Responder solo con el texto final.
`.replace('%s', sourceText);

    return new Promise((resolve, reject) => {
      this.aiTextGenerationService.enhanceOrGenerate(text, idMarca, 'instruccion').subscribe({
        next: (res) => resolve(res.mensaje),
        error: () => {
          if (!this.trunkBaseConfigurationService.CIABodyLoadingRequests) this.dynamicComponentService.destroyBodyLoading();
          reject('Error generating strict template Dos');
        }
      });
    });
  }

  public generateStrictTemplateTresFromText(sourceText: string, idMarca: string): Promise<string> {
    const text = `Reescribe el siguiente texto '%s' para una plantilla utilitaria de WhatsApp.

Reglas de la plantilla Tres (variacion fuerte):
- Mantener intacto cualquier contenido entre [].
- No agregar ni eliminar contenidos entre [].
- Hacer una reescritura profunda: cambia estructura, ritmo y redaccion de forma notable.
- Debe verse claramente distinta a la plantilla original y a la variante Dos.
- Puedes dividir o unir ideas para que suene natural, directa y humana.
- Mantener intencion utilitaria (atencion/informacion), sin marketing ni venta.
- Prohibido contenido comercial: promociones, ofertas, descuentos o lenguaje persuasivo.
- Responder solo con el texto final.
`.replace('%s', sourceText);

    return new Promise((resolve, reject) => {
      this.aiTextGenerationService.enhanceOrGenerate(text, idMarca, 'instruccion').subscribe({
        next: (res) => resolve(res.mensaje),
        error: () => {
          if (!this.trunkBaseConfigurationService.CIABodyLoadingRequests) this.dynamicComponentService.destroyBodyLoading();
          reject('Error generating strict template Tres');
        }
      });
    });
  }

  playgroundPhoneExist(phonePlayground: string): Promise<boolean> {
    return new Promise((resolve) => {
      if (phonePlayground === '') {
        if (!this.trunkBaseConfigurationService.CIABodyLoadingRequests) this.dynamicComponentService.destroyBodyLoading();
        resolve(false);
        this.openAlert(this.translateService.instant('phone_playground_error', 'No se pudo obtener el telefono de playground'), this.translateService.instant('not_send_template', 'No se ha realizado la acción'), AlertType.error, true)
      }
      resolve(true);
    })
  }
}

